import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import type { Result } from "maz-ui/components/MazPhoneNumberInput";
import { ref } from "vue";
import type {
  Category,
  DegreeSeekingProgram,
  InterestArea
} from "~/server/resolvers/resolvers-types";
import type { CategoryProgramsMap } from "~/src/services/InterestAreaRfiService";
import InterestAreaRfiService from "~/src/services/InterestAreaRfiService";
import type { RfiFormFieldShape } from "~/src/services/rfiSchema";
import { rfiZodSchema } from "~/src/services/rfiSchema";
import type { FormattedProgram, RelationalFormattedProgram, WithRequired } from "~/types/types";
import { getLeadApiRejectionReport, parseLeadApiErrors } from "~/src/services/LeadApiErrorService";
import { useRfiService } from "./useRfiService";
import { useTimer } from "./useTimer";

type DegreeTypeChangeArgs = {
  isStudyPage?: boolean;
  programList?: DegreeSeekingProgram[];
  isDegreePage?: boolean;
  enableUndecidedFlow?: boolean;
  // These props are used to set the program on pages that don't need a selection ( degree pages )
  // programs cms id
  programId?: string;
  programName?: string;
  career?: string;
};

// only used for setting the initial ref, not used for validation, use RfiFormFieldShape for validation
interface RfiFormFieldsRef {
  degreeType?: null | {
    text: string;
    value: string;
  };
  interestArea?: null | {
    text: string;
    value: string;
  };
  program: null | {
    text: string;
    value: string;
  };
  firstName?: string;
  lastName?: string;
  email?: string;
  phone: {
    number: string | null;
    formattedNumber: string | null;
    nationalNumber: string | null;
    isValid: boolean;
    countryCode: string | null;
  };
  isMilitary: boolean | null;
  cq_req_id: string | null;
  cq_req_ip: string | null;
}

/**
 * @description Contains all logic for validating and submitting forms
 * @param options
 */
export default function (options: Partial<DegreeTypeChangeArgs> = {}) {
  interface RfiData {
    allCategory: Category[];
    allInterestAreaType: InterestArea[];
    allDegreeSeekingProgram: DegreeSeekingProgram[];
  }
  const data = ref<RfiData | undefined>();
  usePageQuery<RfiData>(
    `#graphql
        query{
          allCategory{
                title
          }
          allInterestAreaType(sort: {by:"title", order: "ASC"}){
            title
          }
          allDegreeSeekingProgram(sort: {by:"title", order: "ASC"}, 
          filter: {
            programFeature: {
              # Includes programs with programFeatures that have a title of RFI
              eq: "b4e8dd15-fed3-4594-a24b-ad8147dc9e26" 
            }
          }
          ){
            id
            code
            title
            category{
              title
            }
            interestAreas{
              title
            }
          }
      }`,
    {},
    { key: "rfi-data" }
  )
    .then((payload) => {
      data.value = payload;
    })
    .catch((error: Error) => {
      console.error(error);
    });

  const nuxtApp = useNuxtApp();
  const RfiService = useRfiService();
  const { getElapsedTime } = useTimer();
  // Setting up defaults, unused currently but makes futurue refactor easier
  const optionsWithDefaults = { ...options };
  const hasUndecidedFlow = Boolean(optionsWithDefaults.enableUndecidedFlow);

  const emailTouched = ref(false);
  const emailBlurred = ref(false);
  const formSubmitted = ref(false);
  const isEmailValid = ref<boolean | null>(null);
  const showLoader = ref(false);
  const formDisabled = ref(false);
  const emailApiError = ref("");
  const phoneApiError = ref("");
  const submissionError = ref("");

  const allCategories = ref<string[] | undefined>([]);
  const allInterestAreas = ref<string[] | undefined>([]);
  const allPrograms = ref<WithRequired<FormattedProgram, "interestAreas">[]>([]);
  const categoryProgramsMap = ref<CategoryProgramsMap>({});
  const relationalProgramData = ref<RelationalFormattedProgram>({});
  const degreeTypeOptions = ref<ReturnType<typeof RfiService.getDegreeTypeOptions>>([]);
  const interestAreaOptions = ref<ReturnType<typeof RfiService.getInterestAreaOptions>>([]);
  const programOptions = ref<ReturnType<typeof RfiService.getProgramOptions>>([]);

  const currentProgramCode = ref<string | null>(null);

  const currentProgramName = ref<string | null>(null);

  // initial form object, also stores final form data. Using zod for validation
  const form = ref<RfiFormFieldShape | RfiFormFieldsRef>({
    degreeType: null,
    interestArea: null,
    program: null,
    phone: {
      number: null,
      formattedNumber: null,
      nationalNumber: null,
      isValid: true,
      countryCode: null
    },
    isMilitary: false,
    cq_req_id: null,
    cq_req_ip: null
  });

  /// Error messages Using zod safeparse
  const firstNameErrorMessage = computed(() => {
    if ("firstName" in form.value) {
      const result = rfiZodSchema.shape.firstName.safeParse(form.value.firstName);
      if (!result.success) {
        // Passes back the most recent error, potential to list all validation errors
        return result.error.errors[0].message;
      }
    }
    return "";
  });

  const lastNameErrorMessage = computed(() => {
    if ("lastName" in form.value) {
      const result = rfiZodSchema.shape.lastName.safeParse(form.value.lastName);
      if (!result.success) {
        // Passes back the most recent error, potential to list all validation errors.
        return result.error.errors[0].message;
      }
    }
    return "";
  });

  const emailErrorMessage = computed(() => {
    if (emailApiError.value) {
      return emailApiError.value;
    }
    if (!emailTouched.value && !emailBlurred.value && !formSubmitted.value) {
      return "";
    }
    if ("email" in form.value) {
      if (!form.value.email) {
        return emailBlurred.value || formSubmitted.value
          ? "Email is required. Please enter a valid email address."
          : "";
      }
      const result = rfiZodSchema.shape.email.safeParse(form.value.email);
      if (!result.success) {
        return result.error.errors[0].message;
      }
    }
    return "";
  });

  const validateEmail = () => {
    if ("email" in form.value) {
      const result = rfiZodSchema.shape.email.safeParse(form.value.email);
      isEmailValid.value = result.success;
    }
  };

  const formDataIsValid = computed(() => {
    const result = rfiZodSchema.safeParse(form.value);
    if (result.success) {
      return true;
    } else {
      return false;
    }
  });

  const programIsValid = computed(() => {
    return Boolean(form.value.program?.value);
  });

  const militaryIsValid = computed(() => {
    return typeof form.value.isMilitary === "boolean";
  });

  const selectedDegreeType = computed(() => {
    return form.value.degreeType?.value || optionsWithDefaults.career || null;
  });

  const selectedDegreeTypeAllowsUndecided = computed(() => {
    return hasUndecidedFlow && RfiService.canUseUndecidedProgram(selectedDegreeType.value);
  });

  const selectedDegreeTypeShowsUndecided = computed(() => {
    return hasUndecidedFlow && RfiService.canShowUndecidedOption(selectedDegreeType.value);
  });

  const programIsRequired = computed(() => {
    if (!hasUndecidedFlow) {
      return true;
    }

    return Boolean(selectedDegreeType.value) && !selectedDegreeTypeAllowsUndecided.value;
  });

  const stepOneIsValid = computed(() => {
    if (hasUndecidedFlow) {
      return (
        Boolean(form.value.degreeType?.value) &&
        militaryIsValid.value &&
        (!programIsRequired.value || programIsValid.value)
      );
    }

    return programIsValid.value && militaryIsValid.value;
  });

  const interestAreaDisabled = computed(() => {
    return hasUndecidedFlow ? !form.value.degreeType?.value : false;
  });

  const programDisabled = computed(() => {
    return hasUndecidedFlow ? !form.value.degreeType?.value : false;
  });

  const submitDisabled = computed(() => {
    if (isEmailValid.value === false || form.value.phone.isValid === false) {
      return true;
    } else if (programIsRequired.value && !form.value.program?.value) {
      return true;
    } else if (showLoader.value === true || formDisabled.value === true) {
      return true;
    } else if (formDataIsValid.value === false) {
      return true;
    } else {
      return false;
    }
  });

  const getMilitaryTrackingValue = (isMilitary: boolean | null = form.value.isMilitary) => {
    if (isMilitary === true) {
      return "yes";
    }

    if (isMilitary === false) {
      return "no";
    }

    return "";
  };

  const createOption = (text: string) => ({
    text,
    value: text
  });

  const resolveRfiSelection = () => {
    const selectedDegreeType = form.value.degreeType?.value || optionsWithDefaults.career || null;
    const canResolveUndecided =
      hasUndecidedFlow && RfiService.canUseUndecidedProgram(selectedDegreeType);
    const selectedInterestAreaValue = form.value.interestArea?.value || null;
    const selectedProgramValue = form.value.program?.value || null;
    const selectedInterestAreaIsUndecided =
      RfiService.isUndecidedInterestAreaValue(selectedInterestAreaValue);
    const selectedProgramIsUndecided = RfiService.isUndecidedProgramValue(selectedProgramValue);

    let interestArea =
      form.value.interestArea?.text && form.value.interestArea?.value
        ? {
            text: form.value.interestArea.text,
            value: form.value.interestArea.value
          }
        : null;
    let programCode: string | null = null;
    let programName: string | null = null;

    if (selectedProgramIsUndecided && canResolveUndecided) {
      programCode = RfiService.getUndecidedProgramCode(selectedDegreeType);
      programName = RfiService.undecidedOptionText;
      interestArea = createOption(RfiService.undecidedOptionText);
    } else if (selectedProgramValue && !selectedProgramIsUndecided) {
      const pairedInterestArea = RfiService.getPrimaryInterestAreaByProgramId(
        allPrograms.value,
        selectedProgramValue
      );

      programCode = RfiService.getProgramCode(
        allPrograms.value,
        selectedProgramValue,
        selectedDegreeType
      );
      programCode = programCode === "N/A" ? null : programCode;
      programName = programCode
        ? RfiService.getProgramNameFromCode(allPrograms.value, programCode)
        : null;

      if (!selectedInterestAreaValue || selectedInterestAreaIsUndecided) {
        interestArea = pairedInterestArea ? createOption(pairedInterestArea) : null;
      }
    } else if (canResolveUndecided) {
      programCode = RfiService.getUndecidedProgramCode(selectedDegreeType);
      programName = RfiService.undecidedOptionText;
      interestArea =
        selectedInterestAreaValue && !selectedInterestAreaIsUndecided
          ? {
              text: form.value.interestArea?.text || selectedInterestAreaValue,
              value: selectedInterestAreaValue
            }
          : createOption(RfiService.undecidedOptionText);
    }

    const selection = {
      degreeType: selectedDegreeType,
      interestArea,
      programCode,
      programName
    };

    return selection;
  };

  const syncResolvedStepOneSelection = () => {
    const selection = resolveRfiSelection();

    if (!hasUndecidedFlow || !RfiService.canShowUndecidedOption(selection.degreeType)) {
      return selection;
    }

    const selectedProgramValue = form.value.program?.value || null;
    const selectedInterestAreaValue = form.value.interestArea?.value || null;
    const selectedProgramIsUndecided = RfiService.isUndecidedProgramValue(selectedProgramValue);
    const selectedInterestAreaIsUndecided =
      RfiService.isUndecidedInterestAreaValue(selectedInterestAreaValue);

    if (
      selection.programName === RfiService.undecidedOptionText &&
      selection.programCode &&
      !selectedProgramValue
    ) {
      form.value.program = {
        text: RfiService.undecidedOptionText,
        value: RfiService.undecidedProgramOptionValue
      };
    }

    if (
      selection.interestArea &&
      (!selectedInterestAreaValue || selectedInterestAreaIsUndecided) &&
      (!selectedProgramValue || selectedProgramIsUndecided)
    ) {
      form.value.interestArea = selection.interestArea;
    }

    return selection;
  };

  watch(
    () => form.value.email,
    (newEmail) => {
      // Trigger the email validation whenever email changes
      if (newEmail !== undefined) {
        validateEmail();
      }
    }
  );

  // watching form values to reset based on in the other options are possible
  watch(
    form,
    (newValue) => {
      let programs: FormattedProgram[];
      let interestAreas: string[];
      const hasSelectedDegreeType = Boolean(newValue.degreeType?.value);
      const includeUndecidedOption =
        hasUndecidedFlow && RfiService.canShowUndecidedOption(newValue.degreeType?.value);
      const selectedInterestAreaIsUndecided = RfiService.isUndecidedInterestAreaValue(
        newValue.interestArea?.value
      );

      if (optionsWithDefaults.isStudyPage === true) {
        if (hasSelectedDegreeType) {
          programs = categoryProgramsMap.value[newValue.degreeType!.value!] || [];
        } else if (hasUndecidedFlow) {
          programs = [];
        } else {
          programs = allPrograms.value;
        }

        if (newValue.program && newValue.program.value) {
          const hasSelectedProgram = programs.some((item) => {
            return item.id === newValue.program!.value;
          });
          const selectedProgramIsUndecided = RfiService.isUndecidedProgramValue(
            newValue.program?.value
          );

          if (!hasSelectedProgram && !(includeUndecidedOption && selectedProgramIsUndecided)) {
            newValue.program = null;
          }
        }
      } else {
        if (hasSelectedDegreeType) {
          interestAreas = RfiService.getDegreeTypeInterestAreas(
            allPrograms.value as Array<WithRequired<FormattedProgram, "interestAreas">>,
            newValue.degreeType!.value!
          );
        } else if (hasUndecidedFlow) {
          interestAreas = [];
        } else {
          interestAreas = allInterestAreas.value = RfiService.getAllFormattedInterestAreas(
            data?.value?.allInterestAreaType ?? []
          );
        }

        if (newValue.interestArea && newValue.interestArea.value) {
          const selectedInterestArea = interestAreas.some((interestArea) => {
            return interestArea === newValue.interestArea!.value;
          });

          if (
            !selectedInterestArea &&
            !(includeUndecidedOption && selectedInterestAreaIsUndecided)
          ) {
            newValue.interestArea = null;
          }
        }

        if (
          newValue.interestArea &&
          newValue.interestArea.value &&
          selectedInterestAreaIsUndecided &&
          newValue.degreeType &&
          newValue.degreeType.value
        ) {
          programs = RfiService.getDegreeTypePrograms(
            allPrograms.value as Array<WithRequired<FormattedProgram, "interestAreas">>,
            newValue.degreeType!.value!
          );
        } else if (
          newValue.interestArea &&
          newValue.interestArea.value &&
          newValue.degreeType &&
          newValue.degreeType.value
        ) {
          programs =
            relationalProgramData.value[form.value.degreeType!.value!]?.[
              form.value.interestArea!.value!
            ] || [];
        } else if (newValue.interestArea && newValue.interestArea.value) {
          programs = RfiService.getInterestAreaPrograms(
            allPrograms.value as Array<WithRequired<FormattedProgram, "interestAreas">>,
            newValue!.interestArea!.value
          );
        } else if (newValue.degreeType && newValue.degreeType.value) {
          programs = RfiService.getDegreeTypePrograms(
            allPrograms.value as Array<WithRequired<FormattedProgram, "interestAreas">>,
            newValue.degreeType!.value!
          );
        } else if (hasUndecidedFlow) {
          programs = [];
        } else {
          programs = allPrograms.value;
        }

        if (newValue.program?.value) {
          const selectedProgram = programs.some((program: FormattedProgram) => {
            return program.id === newValue.program!.value;
          });
          const selectedProgramIsUndecided = RfiService.isUndecidedProgramValue(
            newValue.program?.value
          );

          if (!selectedProgram && !(includeUndecidedOption && selectedProgramIsUndecided)) {
            newValue.program = null;
          }
        }

        interestAreaOptions.value = RfiService.getInterestAreaOptions(interestAreas, {
          includeUndecided: includeUndecidedOption
        });
      }

      programOptions.value = RfiService.getProgramOptions(programs, {
        includeUndecided: includeUndecidedOption
      });

      const selection = resolveRfiSelection();
      currentProgramCode.value = selection.programCode;
      currentProgramName.value = selection.programName;
    },
    {
      deep: true
    }
  );

  // functions to handle user input on the degree, interest area and programs
  const handleDegreeTypeChange = () => {
    // degree type gtm event
    analyticsComposable.trackSelectEvent(
      "onclick",
      form.value.degreeType && form.value.degreeType.value ? "click" : "unclick",
      "select degree type",
      "main content",
      "request info",
      form.value.degreeType && form.value.degreeType.value
        ? form.value.degreeType.value!.toLowerCase()
        : "close cross"
    );
  };

  const handleInterestAreaChange = () => {
    // area of interest gtm event
    analyticsComposable.trackSelectEvent(
      "onclick",
      form.value.interestArea && form.value.interestArea.value ? "click" : "unclick",
      "select area of interest",
      "main content",
      "request info",
      form.value.interestArea && form.value.interestArea.value
        ? form.value.interestArea.value.toLowerCase()
        : "close cross"
    );
  };

  const handleProgramChange = () => {
    // program gtm event. Clearing the field resolves the selection back to the
    // undecided fallback, so the cleared state is read from the field itself.
    const cleared = !form.value.program;
    analyticsComposable.trackSelectEvent(
      "onclick",
      cleared ? "unclick" : "click",
      "select desired program",
      "main content",
      "request info",
      cleared ? "close cross" : (currentProgramName.value ?? "").toLowerCase()
    );
  };

  const handleFirstNameBlur = () => {
    const text = form.value.firstName ? form.value.firstName.toLowerCase() : "";
    analyticsComposable.trackFormEvent(
      "onclick",
      "click",
      "blur",
      "main content",
      "request info ^ first name",
      text
    );
  };

  const handleLastNameBlur = () => {
    const text = form.value.lastName ? form.value.lastName.toLowerCase() : "";
    analyticsComposable.trackFormEvent(
      "onclick",
      "click",
      "blur",
      "main content",
      "request info ^ last name",
      text
    );
  };

  const handleEmailBlur = () => {
    emailTouched.value = true;
    emailBlurred.value = true;
    const text = form.value.email ? form.value.email.toLowerCase() : "";
    analyticsComposable.trackFormEvent(
      "onclick",
      "click",
      "blur",
      "main content",
      "request info ^ email",
      text
    );
  };

  const handleEmailInput = () => {
    emailTouched.value = true;
    emailBlurred.value = false;
    isEmailValid.value = null;
    formDisabled.value = false;
    emailApiError.value = "";
    submissionError.value = "";
    validateEmail();
  };

  const handlePhoneBlur = () => {
    analyticsComposable.trackFormEvent(
      "onclick",
      "click",
      "blur",
      "main content",
      "request info ^ phone number",
      form.value.phone.formattedNumber ? form.value.phone.formattedNumber : ""
    );
  };

  const handlePhoneUpdate = (payload: Result) => {
    formDisabled.value = false;

    form.value.phone.formattedNumber = payload.formatInternational!;
    form.value.phone.nationalNumber = payload.nationalNumber!;
    form.value.phone.countryCode = payload.countryCode!;
    form.value.phone.isValid = payload.isValid;
    phoneApiError.value = "";
    submissionError.value = "";
  };

  const handleMilitaryClick = () => {
    analyticsComposable.trackSelectEvent(
      "onclick",
      "click",
      "checkbox",
      "main content",
      "request info ^ military or military dependent",
      getMilitaryTrackingValue()
    );
  };

  const handleSubmit = () => {
    formSubmitted.value = true;
    showLoader.value = true;
    emailApiError.value = "";
    phoneApiError.value = "";
    submissionError.value = "";

    const timeSpentSeconds = getElapsedTime();

    const selection = resolveRfiSelection();

    if (!selection.programName || !selection.programCode) {
      showLoader.value = false;
      console.error("program name or code missing");
      return;
    }

    const career =
      selection.degreeType ||
      RfiService.getCareerValueByProgramCode(selection.programCode, allPrograms.value);

    if (!career) {
      showLoader.value = false;
      console.error("career missing");
      return;
    }

    const formTrackingData = RfiService.getFormTrackingData(
      "request info",
      "submit",
      form.value as RfiFormFieldShape,
      career,
      selection.programName,
      selection.programCode
    );
    analyticsComposable.trackEvent(formTrackingData);
    handleLeadSubmission(timeSpentSeconds);
  };

  // A 400 means the lead API rejected the visitor's input (sentryFilters drops
  // the bare httpClient event for it). Report which fields were rejected, never
  // their values, so each cause is its own Sentry issue. No `cause` is attached:
  // the FetchError carries the response body. See PDASUOW-26393.
  const reportLeadApiRejection = (error: unknown) => {
    const report = getLeadApiRejectionReport(error);
    if (!report) return;

    nuxtApp.$sentryCaptureException?.(new Error(report.message), {
      level: "warning",
      fingerprint: report.fingerprint
    });
  };

  const applyLeadApiErrors = (error: unknown) => {
    const messages = parseLeadApiErrors(error);
    emailApiError.value = messages.email;
    phoneApiError.value = messages.phone;
    submissionError.value = messages.general;
  };

  const handleLeadSubmission = async (timeSpentSeconds: number) => {
    const selection = resolveRfiSelection();
    const career =
      selection.degreeType ||
      (selection.programCode
        ? RfiService.getCareerValueByProgramCode(selection.programCode, allPrograms.value)
        : null);

    if (!selection.programName || !selection.programCode || !career) {
      console.error("Program name or code missing");
      return;
    }

    const formTrackingData = RfiService.getMilitaryFormTrackingData(
      "request info",
      "submit",
      form.value as RfiFormFieldShape,
      career,
      selection.programName,
      selection.programCode,
      form.value.isMilitary as boolean
    );

    let leadData: ReturnType<typeof RfiService.getMilitaryLeadData>;
    const validatedFormData = rfiZodSchema.safeParse(form.value);
    const isInternational = form.value.phone.countryCode === "US" ? 0 : 1;
    const isMilitary = form.value.isMilitary === false ? 0 : 1;

    if (!validatedFormData.success) {
      console.error(
        "form validation issue, falling back to default form data",
        validatedFormData.error.errors[0].message
      );
      leadData = RfiService.getMilitaryLeadData(
        form.value as RfiFormFieldShape,
        selection.programCode,
        {
          degreeType: career,
          areaOfInterest: selection.interestArea?.text || "",
          programName: selection.programName
        }
      );
    } else {
      leadData = RfiService.getMilitaryLeadData(
        validatedFormData.data as RfiFormFieldShape,
        selection.programCode,
        {
          degreeType: career,
          areaOfInterest: selection.interestArea?.text || "",
          programName: selection.programName
        }
      );
    }

    (leadData as any).rfi_timer_flag = timeSpentSeconds <= 20;
    (leadData as any).rfi_timer_duration = timeSpentSeconds;
    try {
      formDisabled.value = true;

      await RfiService.addRFILead(leadData);

      // @ts-expect-error -- military_service is boolean, AnalyticsEventObject expects strings
      analyticsComposable.trackEvent({
        ...formTrackingData,
        event: "form",
        name: "onsubmit",
        action: "rfi submit",
        type: "submit",
        text: "rfi form submitted"
      });

      setTimeout(async () => {
        showLoader.value = false;
        if (timeSpentSeconds < 20) {
          await navigateTo("/degree-program-request-info-thank-you/no-contact-thank-you-page", {
            open: { target: "_self" }
          });
          return;
        }

        if (career === "Undergraduate") {
          await navigateTo({
            path: "/degree-program-request-info-thank-you/undergrad",
            query: {
              military: isMilitary.toString(),
              international: isInternational.toString(),
              program: selection.programCode,
              name: form.value.firstName
            }
          });
        } else if (career === "Graduate") {
          await navigateTo({
            path: "/degree-program-request-info-thank-you/graduate",
            query: {
              military: isMilitary.toString(),
              international: isInternational.toString(),
              program: selection.programCode,
              name: form.value.firstName
            }
          });
        } else {
          await navigateTo("/degree-program-request-info-thank-you", {
            open: {
              target: "_self"
            }
          });
        }
      }, 2000);
    } catch (error) {
      // @ts-expect-error -- military_service is boolean, AnalyticsEventObject expects strings
      analyticsComposable.trackEvent({
        ...formTrackingData,
        text: "rfi form submission error"
      });
      console.error(error);
      reportLeadApiRejection(error);
      applyLeadApiErrors(error);
      showLoader.value = false;
      formDisabled.value = false;
    }
  };

  const loadInitialOptions = () => {
    if (options.programName && options.programId) {
      form.value.program = {
        value: options.programId,
        text: options.programName
      };
    } else if (options.programName || options.programId) {
      console.error("missing either program name or program code when setting explicitly");
    }

    // don't need categories & interest areas for degree page rfi
    allCategories.value = RfiService.getAllFormattedCategories(data?.value?.allCategory ?? []);
    allInterestAreas.value = RfiService.getAllFormattedInterestAreas(
      data?.value?.allInterestAreaType ?? []
    );
    if (optionsWithDefaults.isStudyPage) {
      if (optionsWithDefaults.programList) {
        allPrograms.value = InterestAreaRfiService.generateFormattedProgramCollection(
          optionsWithDefaults.programList
        );
      } else {
        console.error("Program list is required when isStudyPage is true");
      }

      categoryProgramsMap.value = InterestAreaRfiService.generateCategoryProgramsMap(
        allCategories.value!,
        allPrograms.value
      );
    } else {
      allPrograms.value = RfiService.getAllFormattedPrograms(
        data?.value?.allDegreeSeekingProgram ?? []
      );
      // relationalProgramData
      relationalProgramData.value = RfiService.getRelationalProgramData(
        allCategories.value!,
        allPrograms!.value!
      );
    }

    // initial options
    degreeTypeOptions.value = RfiService.getDegreeTypeOptions(allCategories.value!);
    interestAreaOptions.value =
      hasUndecidedFlow && !form.value.degreeType?.value
        ? []
        : RfiService.getInterestAreaOptions(allInterestAreas.value!, {
            includeUndecided: selectedDegreeTypeShowsUndecided.value
          });
    programOptions.value =
      hasUndecidedFlow && !form.value.degreeType?.value
        ? []
        : RfiService.getProgramOptions(allPrograms.value, {
            includeUndecided: selectedDegreeTypeShowsUndecided.value
          });
  };

  watch(
    data,
    (newData) => {
      if (
        newData?.allCategory &&
        newData?.allDegreeSeekingProgram &&
        newData?.allInterestAreaType
      ) {
        loadInitialOptions();
      }
    },
    { once: true }
  );

  return {
    form,
    isEmailValid,
    emailErrorMessage,
    phoneApiError,
    submissionError,
    firstNameErrorMessage,
    lastNameErrorMessage,
    programIsValid,
    programIsRequired,
    stepOneIsValid,
    formDataIsValid,
    submitDisabled,
    showLoader,
    formDisabled,
    interestAreaDisabled,
    programDisabled,
    handleDegreeTypeChange,
    allCategories,
    allInterestAreas,
    allPrograms,
    programOptions,
    interestAreaOptions,
    degreeTypeOptions,
    relationalProgramData,
    currentProgramCode,
    currentProgramName,
    handleInterestAreaChange,
    handleProgramChange,
    handleFirstNameBlur,
    handleLastNameBlur,
    handleEmailBlur,
    handleEmailInput,
    validateEmail,
    handlePhoneBlur,
    handlePhoneUpdate,
    handleMilitaryClick,
    syncResolvedStepOneSelection,
    handleSubmit,
    handleLeadSubmission
  };
}
